import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, PhoneInputGroup, SearchableSelect, Avatar } from '@/backoffice/components/ui';
import {
  fetchUserProfile,
  updateUserProfile,
  type UserProfile,
} from '@/domains/client/services/client.service';
import { fetchZones } from '@/domains/admin/services/admin.service';
import { formatPhoneNumber, splitPhoneNumber } from '@/utils/phone';
import { Loader2, Save, User } from 'lucide-react';

export function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [zones, setZones] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [firstnames, setFirstnames] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+224');
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);

  useEffect(() => {
    Promise.all([fetchUserProfile(), fetchZones()])
      .then(([user, zoneList]) => {
        setProfile(user);
        setFirstnames(user.firstnames);
        setLastname(user.lastname);
        setEmail(user.email ?? '');
        setZoneId(user.zone?.id ?? null);

        const split = splitPhoneNumber(user.phone);
        setPhoneCountryCode(split.countryCode);
        setPhone(split.number);

        setZones(zoneList.map((z) => ({ id: z.id, name: z.name })));
      })
      .catch(() => setError('Impossible de charger votre profil.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!firstnames.trim() || !lastname.trim() || !phoneValid) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const zone = zones.find((z) => z.id === zoneId);
      const updated = await updateUserProfile({
        firstnames: firstnames.trim(),
        lastname: lastname.trim(),
        email: email.trim() || null,
        phone: formatPhoneNumber(phone, phoneCountryCode),
        zone: zone ? { id: zone.id, name: zone.name } : profile?.zone,
      });
      setProfile(updated);
      setSuccess('Profil mis à jour avec succès.');
    } catch {
      setError('La mise à jour du profil a échoué.');
    } finally {
      setSaving(false);
    }
  };

  const fullName = [firstnames, lastname].filter(Boolean).join(' ').trim();

  return (
    <DashboardLayout
      userType="client"
      title="Mon profil"
      breadcrumbs={[
        { label: 'Tableau de bord', path: '/dashboard/client' },
        { label: 'Mon profil' },
      ]}
    >
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-6">
          <Card>
            <div className="flex flex-col sm:flex-row items-center gap-6 p-6">
              <Avatar name={fullName || 'Utilisateur'} size="xl" />
              <div className="text-center sm:text-left">
                <h2 className="text-xl font-bold text-gray-900">{fullName || 'Utilisateur'}</h2>
                <p className="text-gray-500 mt-1">{profile?.phone}</p>
                {profile?.zone?.name && (
                  <p className="text-sm text-teal-600 mt-1">{profile.zone.name}</p>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-900">Informations personnelles</h3>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              {success && (
                <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Prénom(s)"
                  value={firstnames}
                  onChange={(e) => setFirstnames(e.target.value)}
                  required
                />
                <Input
                  label="Nom"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  required
                />
              </div>

              <Input
                label="Adresse e-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="exemple@email.com"
              />

              <PhoneInputGroup
                label="Téléphone"
                value={phone}
                countryCode={phoneCountryCode}
                onChange={(value, isValid) => {
                  setPhone(value);
                  setPhoneValid(isValid);
                }}
                onCountryCodeChange={setPhoneCountryCode}
                required
              />

              <SearchableSelect
                label="Zone principale"
                options={zones.map((z) => ({ value: String(z.id), label: z.name }))}
                value={zoneId != null ? String(zoneId) : ''}
                onChange={(value) => setZoneId(value ? Number(value) : null)}
                placeholder="Sélectionnez votre zone"
              />

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving || !firstnames.trim() || !lastname.trim() || !phoneValid}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
